import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type StatusJointPaletteDb = {
  jointPaletteId: string;
  hostUserId: string;
  gestUserId: string;
  joined: boolean;
}
const initial: StatusJointPaletteDb = {
  jointPaletteId: '',
  hostUserId: '',
  gestUserId: '',
  joined: false
};

export type StatusJointPaletteDbHashType = {
  [key in string]: StatusJointPaletteDb;
}

export type StatusJointPaletteAddPayload = {
  id?: string;
} & StatusJointPaletteDb;
export type StatusJointPaletteUpdatePayload = {
  id: string;
} & { [P in keyof StatusJointPaletteDb]?: StatusJointPaletteDb[P] };

export type StatusJointPaletteDbState = {
  statusJointPalettes: StatusJointPaletteDbHashType;
}

export const generateStatusJointPaletteId = (): string => generateId('sjp');
let initialState: StatusJointPaletteDbState = { statusJointPalettes: {} };
const key = 'statusJointPalettes';

const statusJointPaletteDbSlice = createSlice({
  name: 'statusJointPaletteDb',
  initialState,
  reducers: {
    initializeStatusJointPalettes(state: StatusJointPaletteDbState) {
      state.statusJointPalettes = {};
    },
    addStatusJointPalette(state: StatusJointPaletteDbState, action: PayloadAction<StatusJointPaletteAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateStatusJointPaletteId();
      state.statusJointPalettes[id] = obj;
      saveData(key, id, obj);
    },
    updateStatusJointPalette(state: StatusJointPaletteDbState, action: PayloadAction<StatusJointPaletteUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.statusJointPalettes[id] === undefined) {
        console.log(`*** StatusJointPalette not found: ${id}`);
      } else {
        const newData = { ...state.statusJointPalettes[id], ...obj };
        state.statusJointPalettes[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteStatusJointPalette(state: StatusJointPaletteDbState, action: PayloadAction<StatusJointPaletteUpdatePayload>) {
      const id = action.payload.id;
      delete state.statusJointPalettes[id];
      deleteData(key, id);
    },
    saveStatusJointPalettes(state: StatusJointPaletteDbState) {
      saveAllData(key, state.statusJointPalettes);
    },
    signUpStatusJointPalettes(state: StatusJointPaletteDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.statusJointPalettes, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: StatusJointPaletteDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.statusJointPalettes = updatedValues;
      if (changed) saveAllData(key, state.statusJointPalettes);
    })
  }
})

export const { initializeStatusJointPalettes, addStatusJointPalette, updateStatusJointPalette, deleteStatusJointPalette, saveStatusJointPalettes, signUpStatusJointPalettes } = statusJointPaletteDbSlice.actions;
export default statusJointPaletteDbSlice.reducer;
